/**
 * API de recetas: las emite el médico al cerrar la consulta, las despacha
 * farmacia. Es la bisagra entre el consultorio y el mostrador.
 */
import { ApiError, apiFetch } from "./client";

export type EstadoReceta =
  | "PENDIENTE"
  | "PARCIAL"
  | "DISPENSADA"
  | "ANULADA"
  /** Cerrada sin pasar por el mostrador: la compró fuera o no volvió. */
  | "NO_RETIRADA";

export interface RecetaItem {
  id: number;
  producto_id: number | null;
  descripcion: string;
  dosis: string | null;
  frecuencia: string | null;
  duracion: string | null;
  indicacion: string | null;
  cantidad: number;
  cantidad_dispensada: number;
  pendiente: number;
  /** Stock en el anaquel ahora mismo. null si el ítem no es del catálogo. */
  stock_disponible: number | null;
  /** El que había cuando se recetó, congelado. */
  stock_al_recetar: number | null;
  /** La farmacia podía servirlo entero ese día. Si no, se compra fuera. */
  en_farmacia: boolean;
}

export interface Receta {
  id: number;
  episodio_id: number;
  paciente_id: number;
  paciente_nombre: string;
  profesional_id: number;
  profesional_nombre: string | null;
  fecha: string;
  estado: EstadoReceta;
  indicaciones: string | null;
  items: RecetaItem[];
  motivo_cierre: string | null;
  /** Tiene subido el escaneo con la firma de puño y letra del médico. */
  tiene_firma: boolean;
  /**
   * Si todavía se puede reescribir tal cual, sin emitir otra.
   *
   * Lo decide el servidor: sin imprimir, sin firmar, sin dispensar y en estado
   * PENDIENTE. La pantalla pregunta y obedece en vez de recalcular las cuatro
   * condiciones por su cuenta.
   */
  editable: boolean;
  /** Emitida DESPUÉS de firmar la atención. */
  posterior_al_cierre: boolean;
  /** El reparto ya contado: lo que había en farmacia y lo que no. */
  total_en_farmacia: number;
  total_fuera: number;
}

/** Un medicamento recetado que la farmacia no podía servir. */
export interface DemandaNoCubierta {
  producto_id: number | null;
  descripcion: string;
  /** false = ni siquiera se vende: no es reponer, es empezar a tenerlo. */
  en_catalogo: boolean;
  veces: number;
  cantidad_pedida: number;
  ultima_vez: string | null;
}

export interface RecetaItemIn {
  producto_id?: number | null;
  descripcion?: string | null;
  dosis?: string | null;
  frecuencia?: string | null;
  duracion?: string | null;
  indicacion?: string | null;
  cantidad: number;
}

/** Reescribe una receta que todavía no salió del consultorio. */
export function editarReceta(
  id: number,
  payload: { indicaciones?: string | null; items: RecetaItemIn[] },
): Promise<Receta> {
  return apiFetch<Receta>(`/v1/recetas/${id}`, {
    method: "PUT",
    body: payload,
  });
}

export interface RecetaCreate {
  episodio_id: number;
  /** Añadir a una atención ya firmada. El backend responde 409 sin esto. */
  confirmar_posterior?: boolean;
  indicaciones?: string | null;
  items: RecetaItemIn[];
}

/** Lo que el médico ve de la farmacia al recetar: qué hay y cuánto queda. */
export interface ProductoParaRecetar {
  id: number;
  nombre: string;
  principio_activo: string | null;
  concentracion: string | null;
  forma_farmaceutica: string | null;
  presentacion: string | null;
  requiere_receta: boolean;
  es_controlado: boolean;
  stock: number;
}

export function buscarParaRecetar(q: string): Promise<ProductoParaRecetar[]> {
  return apiFetch<ProductoParaRecetar[]>(
    `/v1/recetas/_catalogo?q=${encodeURIComponent(q)}`,
  );
}

export function crearReceta(payload: RecetaCreate): Promise<Receta> {
  return apiFetch<Receta>("/v1/recetas", { method: "POST", body: payload });
}

export function listRecetas(
  params: {
    paciente_id?: number;
    episodio_id?: number;
    estado?: string;
    pendientes?: boolean;
    sede_id?: number;
  } = {},
): Promise<Receta[]> {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null) p.set(k, String(v));
  });
  const s = p.toString();
  return apiFetch<Receta[]>(`/v1/recetas${s ? `?${s}` : ""}`);
}

export function getReceta(id: number, sede_id?: number): Promise<Receta> {
  return apiFetch<Receta>(
    `/v1/recetas/${id}${sede_id ? `?sede_id=${sede_id}` : ""}`,
  );
}

/**
 * Borra una receta emitida por error. De la base, no la marca anulada.
 *
 * No existe "receta anulada" como cosa que mirar después: existe el médico que
 * se equivocó al escribirla. Guardarla tachada llenaba la historia de ruido y
 * dejaba un documento imprimible que ya no vale.
 */
export function eliminarReceta(id: number): Promise<void> {
  return apiFetch<void>(`/v1/recetas/${id}`, { method: "DELETE" });
}

export function anularReceta(id: number, motivo: string): Promise<Receta> {
  return apiFetch<Receta>(`/v1/recetas/${id}/anular`, {
    method: "POST",
    body: { motivo },
  });
}

/** La saca de la cola de farmacia sin despacharla. No anula la indicación. */
export function cerrarNoRetirada(id: number, motivo?: string): Promise<Receta> {
  return apiFetch<Receta>(`/v1/recetas/${id}/no-retirada`, {
    method: "POST",
    body: { motivo: motivo ?? null },
  });
}

export function demandaNoCubierta(dias = 90): Promise<DemandaNoCubierta[]> {
  return apiFetch<DemandaNoCubierta[]>(
    `/v1/recetas/_demanda-no-cubierta?dias=${dias}`,
  );
}


/**
 * Abre la receta en PDF para imprimirla o entregarla.
 *
 * Se baja con fetch y se abre como blob en lugar de enlazar la URL: un <a> no
 * manda la cabecera de autorización, y meter el token en la dirección lo
 * dejaría escrito en el historial y en los logs del servidor.
 */
/** Qué medicamentos lleva el papel. La receta es UNA; esto es cómo se imprime. */
export type CorteReceta = "completa" | "policlinico" | "fuera";

export async function abrirRecetaPdf(
  recetaId: number,
  opciones: { corte?: CorteReceta; firma?: boolean } = {},
): Promise<void> {
  const { corte = "completa", firma = true } = opciones;
  const { getTokens } = await import("../auth/tokens");
  const token = getTokens()?.access_token;
  const params = new URLSearchParams();
  if (corte !== "completa") params.set("corte", corte);
  if (!firma) params.set("firma", "false");
  const qs = params.toString();
  const res = await fetch(
    `/api/v1/recetas/${recetaId}/pdf${qs ? `?${qs}` : ""}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!res.ok) throw new ApiError(res.status, null, "No se pudo abrir la receta");
  const url = URL.createObjectURL(await res.blob());
  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}


/** Sube la foto o el escaneo de la receta firmada a mano. */
export async function subirRecetaFirmada(
  recetaId: number,
  file: File,
): Promise<Receta> {
  const { getTokens } = await import("../auth/tokens");
  const token = getTokens()?.access_token;
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`/api/v1/recetas/${recetaId}/firmada`, {
    method: "POST",
    // Sin Content-Type: el navegador pone el suyo con el boundary. Ponerlo a
    // mano rompe la subida de forma silenciosa.
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  });
  if (!res.ok) {
    let detalle = "No se pudo subir la receta firmada.";
    try {
      detalle = (await res.json()).detail ?? detalle;
    } catch {
      // se queda el mensaje por defecto
    }
    throw new ApiError(res.status, { detail: detalle });
  }
  return (await res.json()) as Receta;
}
